import { useEffect, useState } from "react";
import { ChevronDown, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { RenderInformativa } from "@/pages/Privacy";

/**
 * Riquadro di presa visione dell'informativa privacy.
 *
 * Perché il testo è QUI dentro e non dietro un link: chi compila un modulo per
 * il proprio figlio non apre una seconda scheda. Un link a un'informativa è
 * formalmente un adempimento assolto e sostanzialmente un documento mai letto.
 * Il pannello espandibile lascia la pagina leggibile e mette comunque il testo
 * a un tocco di distanza, senza uscire dal modulo.
 *
 * Il rendering passa da RenderInformativa, che costruisce SOLO nodi di testo:
 * niente dangerouslySetInnerHTML. Il testo è scritto da un amministratore, ma
 * un'informativa non ha alcun bisogno di HTML e concederlo aprirebbe una via
 * di XSS su una pagina pubblica non autenticata.
 *
 * onChange riceve { presaVisione, versione, hash }: chi usa il componente
 * inoltra versione e hash al backend, così resta registrato QUALE testo è
 * stato mostrato.
 */
export default function InformativaBox({
  tipo = "iscritti",
  valore = false,
  onChange,
  etichetta = "Dichiaro di aver letto l'informativa sul trattamento dei dati",
  testId = "informativa-box",
}) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [aperto, setAperto] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get(`/informativa/${tipo}`)
      .then((r) => { if (vivo) { setDati(r.data); setErrore(false); } })
      .catch(() => { if (vivo) setErrore(true); });
    return () => { vivo = false; };
  }, [tipo]);

  const spunta = (checked) => {
    onChange?.({
      presaVisione: checked,
      versione: dati?.versione ?? null,
      hash: dati?.hash ?? null,
    });
  };

  const dataPubblicazione = dati?.pubblicata_il
    ? new Date(dati.pubblicata_il).toLocaleDateString("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric",
      })
    : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 mb-4" data-testid={testId}>
      <div className="flex items-start gap-2 mb-2">
        <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-bold text-slate-800 text-sm">
            {dati?.etichetta || "Informativa sul trattamento dei dati"}
          </p>
          {dati && (
            <p className="text-xs text-slate-500 font-semibold" data-testid={`${testId}-versione`}>
              Versione {dati.versione}{dataPubblicazione ? ` del ${dataPubblicazione}` : ""}
            </p>
          )}
        </div>
      </div>

      {errore && (
        <p className="flex items-start gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
          Informativa non disponibile in questo momento. Non completare il modulo:
          riprova più tardi o contatta i responsabili.
        </p>
      )}

      {dati && (
        <>
          <button
            type="button"
            onClick={() => setAperto((a) => !a)}
            className="flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-800 mb-2"
            aria-expanded={aperto}
            data-testid={`${testId}-toggle`}
          >
            {aperto ? "Nascondi il testo" : "Leggi il testo completo"}
            <ChevronDown className={`h-4 w-4 transition-transform ${aperto ? "rotate-180" : ""}`} />
          </button>

          {aperto && (
            <div
              className="max-h-72 overflow-y-auto rounded-xl bg-white border border-slate-200 p-4 mb-3"
              data-testid={`${testId}-testo`}
            >
              <RenderInformativa testo={dati.testo} />
            </div>
          )}
        </>
      )}

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="h-5 w-5 rounded accent-emerald-500 shrink-0 mt-0.5"
          checked={!!valore}
          disabled={!dati}
          onChange={(e) => spunta(e.target.checked)}
          data-testid={`${testId}-checkbox`}
        />
        <span className="text-sm font-semibold text-slate-700">{etichetta}</span>
      </label>
    </div>
  );
}
